import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { suppliersApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import type { Supplier } from '@motra/shared';

export default function SupplierManagement() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const queryClient = useQueryClient();

  const { data: res, isLoading } = useQuery({
    queryKey: ['suppliers', account?.id],
    queryFn: () => suppliersApi.list(account!.id),
    enabled: !!account?.id,
  });

  const suppliers = res?.data ?? [];

  const upsertSupplier = useMutation({
    mutationFn: (data: Partial<Supplier>) => suppliersApi.upsert(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
  });

  const deleteSupplier = useMutation({
    mutationFn: (id: string) => suppliersApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState<Supplier | null>(null);
  const [form, setForm] = useState<Partial<Supplier>>({});
  const [formError, setFormError] = useState('');

  function openEdit(supplier: Supplier | null) {
    setEditSupplier(supplier);
    setForm(supplier ? { ...supplier } : {});
    setFormError('');
    setModalOpen(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!form.name?.trim()) {
      setFormError('Name is required.');
      return;
    }

    try {
      await upsertSupplier.mutateAsync(form);
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  const columns = [
    { header: t('name', 'Name'), accessor: 'name' as keyof Supplier },
    { header: 'Contact', accessor: 'contact' as keyof Supplier },
    { header: t('email', 'Email'), accessor: 'email' as keyof Supplier },
    { header: t('address', 'Address'), accessor: 'address' as keyof Supplier },
    {
      header: 'Actions',
      accessor: 'id' as keyof Supplier,
      render: (_v: unknown, row: Supplier) => (
        <div className="action-btns">
          <button
            className="btn btn--sm btn--outline"
            onClick={(e) => { e.stopPropagation(); openEdit(row); }}
          >
            {t('edit', 'Edit')}
          </button>
          <button
            className="btn btn--sm btn--danger"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this supplier?')) deleteSupplier.mutate(row.id);
            }}
          >
            {t('delete', 'Delete')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('suppliers', 'Suppliers')}</h1>
        <button className="btn btn--primary" onClick={() => openEdit(null)}>
          + {t('add', 'Add Supplier')}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={suppliers}
        loading={isLoading}
        rowKey={(r) => r.id}
        emptyMessage="No suppliers"
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editSupplier ? 'Edit Supplier' : 'Add Supplier'}
      >
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">{t('name', 'Name')} *</label>
            <input
              type="text"
              className="form-input"
              value={form.name ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Contact Person</label>
            <input
              type="text"
              className="form-input"
              value={form.contact ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, contact: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('email', 'Email')}</label>
            <input
              type="email"
              className="form-input"
              value={form.email ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('address', 'Address')}</label>
            <textarea
              className="form-textarea"
              value={form.address ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              rows={2}
            />
          </div>

          {formError && <div className="alert alert--error">{formError}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn--outline" onClick={() => setModalOpen(false)}>
              {t('cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={upsertSupplier.isPending}>
              {upsertSupplier.isPending ? 'Saving...' : t('save', 'Save')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
